import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Local-storage key the superuser uses to scope every request to one hospital.
// (Admins/users are already scoped server-side by their own `hospital_id`.)
export const ACTIVE_HOSPITAL_KEY = "hms.active_hospital_id";
export function getActiveHospitalId() {
  try { return localStorage.getItem(ACTIVE_HOSPITAL_KEY) || ""; }
  catch { return ""; }
}
export function setActiveHospitalId(hid) {
  try {
    if (hid) localStorage.setItem(ACTIVE_HOSPITAL_KEY, hid);
    else localStorage.removeItem(ACTIVE_HOSPITAL_KEY);
  } catch { /* no-op */ }
}

// Inject X-Hospital-Id on every request so the backend's scope filter
// can narrow superuser views to one hospital.
api.interceptors.request.use((config) => {
  const hid = getActiveHospitalId();
  if (hid) {
    config.headers = config.headers || {};
    config.headers["X-Hospital-Id"] = hid;
  }
  return config;
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function pdfUrl(path) {
  return `${API_BASE}${path}`;
}

// Authenticated PDF print: fetch with cookies and trigger native print dialog.
// Uses a hidden iframe so the print dialog opens directly without leaving the page.
export async function printPdf(path) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.src = url;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (err) {
        // some browsers block cross-origin print on blob iframes — fall back to download
        const a = document.createElement("a");
        a.href = url;
        a.download = "document.pdf";
        a.target = "_blank";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    };
    setTimeout(() => {
      try { document.body.removeChild(iframe); } catch { /* iframe already gone */ }
      window.URL.revokeObjectURL(url);
    }, 60_000);
    return true;
  } catch (e) {
    const detail = e?.response?.data;
    if (detail instanceof Blob) {
      const txt = await detail.text();
      try { return Promise.reject(new Error(JSON.parse(txt).detail || txt)); }
      catch { return Promise.reject(new Error(txt)); }
    }
    return Promise.reject(e);
  }
}
// Direct download is the most reliable approach across browsers (no popup
// blockers, no SameSite=None cookie issues with target=_blank). Users can
// then open the downloaded PDF natively.
export async function openPdf(path, filename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "document.pdf";
    a.rel = "noreferrer";
    // also try opening in new tab as a non-blocking convenience
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    return true;
  } catch (e) {
    const detail = e?.response?.data;
    if (detail instanceof Blob) {
      const txt = await detail.text();
      try { return Promise.reject(new Error(JSON.parse(txt).detail || txt)); }
      catch { return Promise.reject(new Error(txt)); }
    }
    return Promise.reject(e);
  }
}
